import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { getFromApi } from "utils/api/postToApi";
import { setCesiumMode } from "stores/genericStore/actions";
import { useGenericDispatch } from "stores/genericStore/GenericContext";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import { TrackRaw, TrackWithMetadata } from "types/app";
import { parseTrackFromAPI } from "utils/parsers/trackParser";
import { getTracksWithMetadata } from "utils/tracks/getTracksWithMetadata";
import { useAuth } from "./useAuth";
import { useEditMode } from "./useEditMode";
import { useGenericModals } from "../hooks/useGenericModals";
import { useUserTracks } from "./useUserTracks";
import { useMapContext } from "./useMapContext";

interface PublicTrackData {
  tracks: TrackWithMetadata[] | null;
  selectedTrack: TrackWithMetadata | null;
  selectTrack: (id: number | null) => void;
}

const PublicTrackContext = React.createContext<PublicTrackData>({
  tracks: null,
  selectedTrack: null,
  selectTrack: () => null,
});

export const useProvidePublicTrackData = (): PublicTrackData => {
  const [tracks, setTracks] = useState<TrackWithMetadata[] | null>(null);
  const [filteredTracks, setFilteredTracks] = useState<
    TrackWithMetadata[] | null
  >(null);
  const { editMode, setEditMode } = useEditMode();
  const { openConfirmLosingTrackModal } = useGenericModals();
  const { leaveOpenTrack } = useUserTracks();
  const genericDispatch = useGenericDispatch();
  const { user } = useAuth();
  const {
    setEditedTrack,
    openedTrack,
    state: { trackWaypoints },
  } = useRouting();
  const {
    state: { language },
  } = useOptions();
  const { map } = useMapContext();
  const tracksRef = useRef(tracks);
  const openedTrackRef = useRef(openedTrack);
  const hasWaypointsRef = useRef(trackWaypoints.length > 0);
  const editModeRef = useRef(editMode);
  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);
  useEffect(() => {
    openedTrackRef.current = openedTrack;
  }, [openedTrack]);
  useEffect(() => {
    hasWaypointsRef.current = trackWaypoints.length > 0;
  }, [trackWaypoints.length]);
  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  const {
    state: { showPublicTracks },
  } = useOptions();

  useEffect(() => {
    if (!tracks) return;
    // Filtering tracks to not show user's public tracks twice
    const filtered = tracks.filter((track) =>
      user ? track.userId !== user.id : true
    );
    setFilteredTracks(filtered);
  }, [tracks, user]);

  type GetPublicTracksResponse = {
    tracks: TrackRaw[];
  };

  const fetchTracks = useCallback(async () => {
    try {
      const data = await getFromApi<GetPublicTracksResponse>(
        "get-public-tracks",
        language
      );
      const rawTracks = data.tracks;
      const parsedTracks = rawTracks.map((rawTrack) =>
        parseTrackFromAPI(rawTrack)
      );
      const tracksWithMeta = getTracksWithMetadata(parsedTracks);
      setTracks(tracksWithMeta);
      return data.tracks;
    } catch (_error) {
      setTracks(null);
      return console.error("error getting public tracks");
    }
  }, [language]);

  const selectTrack = useCallback(
    (id: number | null) => {
      genericDispatch(setCesiumMode(false));
      if (id === null) {
        setEditedTrack(null);
        return;
      }
      if (!tracksRef.current) throw new Error("Tracks empty");
      const foundTrack = tracksRef.current.find((track) => track.id === id);
      if (!foundTrack) throw new Error("Track not found");

      const select = () => {
        setEditedTrack(foundTrack, map, "public");
        setEditMode("track");
      };

      // Asks only when a track that isn't saved would be replaced: a new one or one opened via a shared link
      // (the user's own tracks are saved after every change, public tracks can't be changed)
      const openedSource = openedTrackRef.current?.source;
      if (
        editModeRef.current === "track" &&
        hasWaypointsRef.current &&
        (!openedSource || openedSource === "shared")
      ) {
        openConfirmLosingTrackModal({ onConfirm: select });
        return;
      }
      // An own track asks when its latest change isn't saved yet
      leaveOpenTrack(select);
    },
    [
      genericDispatch,
      leaveOpenTrack,
      map,
      openConfirmLosingTrackModal,
      setEditMode,
      setEditedTrack,
    ]
  );

  useEffect(() => {
    if (
      showPublicTracks &&
      (!tracksRef.current || tracksRef.current?.length === 0)
    ) {
      void fetchTracks();
    }
    if (!showPublicTracks) setTracks([]);
  }, [fetchTracks, showPublicTracks]);

  // Selected while a public track is open in the editor, so opening another track or emptying the editor unselects it
  const selectedTrack = useMemo(() => {
    if (openedTrack?.source !== "public") return null;
    const foundTrack =
      tracks?.find((value) => value.id === openedTrack.track.id) ??
      openedTrack.track;
    // return a new copy to prevent accident original array mutation
    return { ...foundTrack };
  }, [openedTrack, tracks]);

  return {
    tracks: filteredTracks,
    selectedTrack,
    selectTrack,
  };
};

export const usePublicTracks = () => useContext(PublicTrackContext);

export const PublicTracksDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const userTrackData = useProvidePublicTrackData();
  return (
    <PublicTrackContext.Provider value={userTrackData}>
      {children}
    </PublicTrackContext.Provider>
  );
};
